import * as React from 'react';
import { ReactNode, useContext, useLayoutEffect, useState } from 'react';
import { type PagedModelUsersDTO, UsersDTO } from 'api/admin/models';
import { No, Yes } from 'pages/components/messages';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { AuthContext } from '../../../../../Auth/components/AuthProvider';
import { DataTable, DataTablePageEvent, DataTableStateEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { UsersServiceApi } from '../../../../../api/admin';
import { getAuthMiddleware } from '../../../../../Auth/utils/GetAuthMiddleware';
import moment from 'moment';
import { momentDateFormat, searchDelayDefault } from '../../../../../Settings/config';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { OrcidResult } from '../OrcidResult';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { parseScope } from '../shared';
import { getMandantSettingsByUsername } from '../../../../../util/mandant-settings-utils';
import { ToastContext } from '../../../../../util/providers/ToastProvider';
import { Direction } from '../../../../../util/types';

export interface OrcidSucceededConnectionsProps {
  userName: string;
}

const initialLazyTableState: DataTablePageEvent = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: 'surname',
  sortOrder: 1,
  filters: {},
};

export function OrcidSucceededConnections(props: OrcidSucceededConnectionsProps): ReactNode {
  const institution: string = getMandantSettingsByUsername(props.userName).institution;
  const [showInfo, setShowInfo] = useState<string>(null);

  const intl: IntlShape = useIntl();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [users, setUsers] = useState<UsersDTO[]>([]);
  const [lazyState, setLazyState] = useState<DataTablePageEvent>(initialLazyTableState);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
  const toast = useContext(ToastContext);

  const { actions } = useContext(AuthContext);
  const api: UsersServiceApi = new UsersServiceApi().withMiddleware(getAuthMiddleware(actions));

  useLayoutEffect((): (() => void) => {
    const delayDebounceFn = setTimeout(() => lazyLoadData(), searchDelayDefault);
    return (): void => clearTimeout(delayDebounceFn);
  }, [lazyState, globalFilterValue]);

  const lazyLoadData = (): void => {
    setLoading(true);
    void api
      .searchUserWithValidOrcid({
        institution,
        page: lazyState.page,
        size: lazyState.rows,
        sort: lazyState.sortField || undefined,
        direction: lazyState.sortOrder > 0 ? Direction.ASC : Direction.DESC,
        searchParameter: globalFilterValue,
      })
      .then((pageUsers: PagedModelUsersDTO): void => {
        if (pageUsers) {
          setUsers(pageUsers.content ?? []);
          setTotalRecords(pageUsers.page.totalElements ?? pageUsers.content?.length ?? 0);
        }
        setLoading(false);
      })
      .catch((response): void => {
        setLoading(false);
        toast.error({ severity: 'error', summary: 'Error', detail: 'Loading users failed', type: 'alert', response });
      });
  };

  const onPageChange = (e: DataTableStateEvent): void => setLazyState(e);

  const onSortChange = (e: DataTableStateEvent): void => setLazyState({ ...e, first: 0, page: 0 });

  const onFilterChange = (e: DataTableStateEvent): void => setLazyState({ ...e, first: 0 });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => setGlobalFilterValue(e.target.value);

  const header = (
    <div className="flex justify-content-end">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder={intl.formatMessage({ id: 'search.placeholder' })}
        />
      </IconField>
    </div>
  );

  const onDisconnect = (userDto: UsersDTO): void => {
    toast.confirm({
      message: intl.formatMessage({ id: 'admin.orcid.disconnect.confirm.message' }),
      header: intl.formatMessage({ id: 'admin.orcid.disconnect.confirm.header' }),
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      acceptLabel: intl.formatMessage({ id: 'admin.orcid.disconnect.confirm.accept' }),
      rejectLabel: intl.formatMessage({ id: 'admin.orcid.disconnect.confirm.abort' }),
      accept: async (): Promise<void> => {
        const result: OrcidResult = await disconnectOrcidUser(userDto);
        if (result.message) {
          toast.alert({
            message: intl.formatMessage({ id: 'admin.orcid.disconnect.confirm.error' }),
          });
        } else {
          toast.notify({
            severity: 'success',
            summary: intl.formatMessage({ id: 'admin.orcid.disconnect.confirm.success' }),
          });
        }
        lazyLoadData();
      },
      reject: (): void => {},
    });
  };

  const disconnectOrcidUser = async (userDto: UsersDTO): Promise<OrcidResult> => {
    const token: string | undefined = await actions.getAuthTokenOrRefresh(true);
    if (!token) {
      return { type: 'error', message: intl.formatMessage({ id: 'admin.orcid.disconnect.error.token' }) };
    }
    const orcidId: string | undefined = userDto.orcidAuthorize?.orcidID;
    if (!orcidId) {
      return { type: 'error', message: intl.formatMessage({ id: 'admin.orcid.disconnect.error.id' }) };
    }
    const url: string = `https://${window.location.hostname}/mms3-orcid/orcid/auth/authenticate/${orcidId}/token?access_token=${token}`;
    const result: Response = await fetch(url, { method: 'DELETE' });
    if (!result.ok) {
      return { type: 'error', message: result.statusText };
    }
    return { type: 'success' };
  };

  return (
    <section className="succeeded-connections">
      <h2>{intl.formatMessage({ id: 'orcidAdminArea.tab.succeededConnections' })}</h2>
      <Dialog
        header={showInfo}
        visible={!!showInfo}
        dismissableMask={true}
        style={{ width: '50vw' }}
        breakpoints={{ '992px': '75vw', '641px': '100vw' }}
        onHide={(): void => setShowInfo(null)}
      >
        <p className="m-0">
          <FormattedMessage id={`orcid.scopes.${showInfo}`} />
        </p>
      </Dialog>
      <DataTable
        lazy={true}
        stripedRows={true}
        showGridlines={true}
        dataKey="id"
        paginator={true}
        first={lazyState.first}
        header={header}
        totalRecords={totalRecords}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        filters={lazyState.filters}
        loading={loading}
        rowsPerPageOptions={[10, 25, 50]}
        rows={lazyState.rows}
        globalFilterFields={['username', 'givenname', 'surname']}
        onPage={onPageChange}
        onSort={onSortChange}
        onFilter={onFilterChange}
        value={users}
      >
        <Column
          field="username"
          sortable={true}
          header={intl.formatMessage({ id: 'orcidAdminArea.header.username' })}
        ></Column>
        <Column
          field="surname"
          sortable={true}
          header={intl.formatMessage({ id: 'orcidAdminArea.header.surname' })}
        ></Column>
        <Column field="givenname" header={intl.formatMessage({ id: 'orcidAdminArea.header.givenname' })}></Column>
        <Column
          field="orcidAuthorize.orcidID"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.orcidID' })}
        ></Column>
        <Column
          field="orcidAuthorize.authorizationDate"
          sortable={true}
          header={intl.formatMessage({ id: 'orcidAdminArea.header.orcidAuthorizationDate' })}
          body={(usersDto: UsersDTO): string =>
            usersDto.orcidAuthorize.authorizationDate
              ? moment(usersDto.orcidAuthorize.authorizationDate).format(momentDateFormat)
              : ''
          }
        ></Column>
        <Column
          field="orcidAuthorize.revocationDate"
          sortable={true}
          header={intl.formatMessage({ id: 'orcidAdminArea.header.orcidRevocationDate' })}
          body={(usersDto: UsersDTO): string =>
            usersDto.orcidAuthorize.revocationDate
              ? moment(usersDto.orcidAuthorize.revocationDate).format(momentDateFormat)
              : ''
          }
        ></Column>
        <Column
          field="orcidAuthorize.orcidScopes"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.orcidScopes' })}
          body={(usersDto: UsersDTO): ReactNode[] =>
            usersDto.orcidAuthorize.orcidScopes.map((scope) => parseScope(scope.scope, setShowInfo))
          }
        ></Column>
        <Column
          field="orcidAuthorize.publicUsageOfLimitedOrcidData"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.publicUsageOfLimitedOrcidData' })}
          body={(usersDto: UsersDTO): ReactNode => (usersDto.orcidAuthorize?.publicUsageOfLimitedOrcidData ? Yes : No)}
        ></Column>
        <Column
          field="orcidAuthorize.tokenRevoked"
          header={intl.formatMessage({ id: 'orcidAdminArea.header.tokenRevoked' })}
          body={(usersDto: UsersDTO): ReactNode => (usersDto.orcidAuthorize?.tokenRevoked ? Yes : No)}
        ></Column>
        <Column
          header={''}
          body={(usersDto: UsersDTO): ReactNode => (
            <Button icon={'pi pi-minus'} severity={'danger'} onClick={(): void => onDisconnect(usersDto)} />
          )}
        ></Column>
      </DataTable>
    </section>
  );
}

export default OrcidSucceededConnections;
